import { useFocusEffect } from 'expo-router';
import { useCallback, useState } from 'react';
import type { LayoutChangeEvent, NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

/**
 * Status bar state for a screen with a full-bleed dark hero under a light
 * sheet. `pastHero` flips once the sheet's top edge reaches the status bar
 * (hero height, less the sheet's overlap, less the top inset), so the clock
 * turns dark the moment it would sit on the light sheet. `focused` lets the
 * screen drop its StatusBar while another screen is pushed over it: the
 * screen stays mounted underneath, and expo-status-bar would otherwise keep
 * its light clock on top of that screen.
 */
export function useHeroStatusBar(overlap: number) {
  const { top } = useSafeAreaInsets();
  const [heroHeight, setHeroHeight] = useState(0);
  const [pastHero, setPastHero] = useState(false);
  const [focused, setFocused] = useState(true);

  useFocusEffect(
    useCallback(() => {
      setFocused(true);
      return () => setFocused(false);
    }, []),
  );

  const onHeroLayout = useCallback(
    (e: LayoutChangeEvent) => setHeroHeight(e.nativeEvent.layout.height),
    [],
  );

  const onScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const past = heroHeight > 0 && e.nativeEvent.contentOffset.y > heroHeight - overlap - top;
      setPastHero((was) => (was === past ? was : past));
    },
    [heroHeight, overlap, top],
  );

  return { pastHero, focused, onHeroLayout, onScroll };
}
